import type { ThunkAction, ReduxPersistAction, AppState, APIError, Dispatch } from '../../types/app.types';
import type {
  V2CartResponse,
  PriceCategory,
  TierCategory,
  V2Pharmacy,
  V2Provider,
  V2AddCartItems,
  V2Coupon,
  V2CouponTemplate,
  V2Cart,
  V2CartItem,
  RxSrcType,
} from '../../types/api-v2.types';
import type { PurchaseResponse, V0Order } from '../../types/api-v0.types';
import type { AccountState } from './account-module';
import type { PriceTiers } from './drugs.types';
import type { PharmacyState } from './pharmacy-module';
import { getAccountSummary } from './account-module';
import { getPharmacyId } from './pharmacy-module';
import { Open, openModal } from './modal-module';
import { trackEvent } from '../../utils/analytics';
import { updateBanner } from './notifications-module';
import { optimisticallyUpdateInCartStatus } from './account-medications-module';
import { isFreeDiabetesMedication } from '../../utils/is-free-diabetes-medication';
import { getPharmacyTier, pharmacyTierToNetworkId } from './pharmacy-module';
import { getSubscriptions } from './subscriptions-module';
import type { SubscriptionsState } from './subscriptions-module';
import trackLinkSharePurchase from '../../utils/linkshare-purchase';
import { getSchedules } from './recurring-schedule-module';

export const isMagicTier = (tier: string): boolean => {
  return tier === 'magic';
};
export const isMagicPriceCategory = (tier: string): boolean => {
  if (!tier) return false;
  return tier.toLowerCase().indexOf('magic') > -1;
};
export type V2UserMessage = {
  level: 'info' | 'warning' | 'error';
  message: string;
  title: string | null | undefined;
  url: string | null | undefined;
};
export type Item = {
  id: string;
  medId: string;
  medNameId: string;
  quantity: number;
  name: string;
  alternateName: string;
  formulation: string;
  price: string;
  discountPrice: string;
  coupon: V2Coupon | null | undefined;
  promotionalCategory: string | null | undefined;
  couponTemplate: V2CouponTemplate | null | undefined;
  warning: string | null | undefined;
  isSupersaver: boolean;
  isTelemedEligible: boolean;
  userMessage: V2UserMessage | null | undefined;
  slug: string;
  rxSrcPharmacyId: string | null | undefined;
  rxSrcProviderId: string | null | undefined;
  rxSrcType: RxSrcType | null | undefined;
  prescription: {
    pharmacy: V2Pharmacy | null | undefined;
    provider: V2Provider | null | undefined;
    hasCloudPharmacyPrescription: boolean;
  };
  priceCategory: PriceCategory;
  consultationPrice?: string;
  isRecurring: boolean;
  isAutoRefillEligible: boolean;
};
type UpdateAutoRefillModalState = {
  type: 'cart/UPDATE_AUTO_REFILL_MODAL_STATE';
};
type SetFetching = {
  type: 'cart/SET_FETCHING';
};
type RequestCart = {
  type: 'cart/REQ_CART';
};
export type RequestCartSuccess = {
  type: 'cart/REQ_CART_SUCCESS';
  res: V2Cart;
};
type RequestCartError = {
  type: 'cart/REQ_CART_ERROR';
} & APIError;

/**
 * the thunkActions for addItem and removeItem will resolve to GetCartSuccess/GetCartError
 * since the response is the same.
 */
type AddItem = {
  type: 'cart/ADD_ITEM';
  item: {
    medNameId: string;
    medId: string;
    quantity: number;
  };
};
type RemoveItem = {
  type: 'cart/REMOVE_ITEM';
  medNameId: string;
  medId: string;
};
type CartComparisonSuccess = {
  type: 'cart/GET_COMPARISON_SUCCESS';
  data: any;
};
type ConfirmedPrescriptions = {
  type: 'cart/CONFIRMED_HAS_PRESCRIPTIONS';
  data: Record<string, any>;
};
type ClearConfirmedPrescriptions = {
  type: 'cart/CLEAR_CONFIRMED_HAS_PRESCRIPTIONS';
  medId: string;
  medNameId: string;
};
export type QueuedItem = {
  name: string;
  prices: PriceTiers;
  quantity: number;
  medId: string;
  tier: TierCategory;
};
type QueueItemAction = {
  type: 'cart/QUEUE_ITEM';
  item: QueuedItem;
};
type ClearQueuedItem = {
  type: 'cart/CLEAR_QUEUED_ITEM';
};
type Purchase = {
  type: 'cart/PURCHASE';
};
type PurchaseSuccess = {
  type: 'cart/PURCHASE_SUCCESS';
  data: V0Order;
};
type PurchaseError = {
  type: 'cart/PURCHASE_ERROR';
} & APIError;
type AddCoupon = {
  type: 'cart/coupon/ADD_COUPON';
};
type AddCouponSuccess = {
  type: 'cart/coupon/ADD_COUPON_SUCCESS';
};
type AddCouponError = {
  type: 'cart/coupon/ADD_COUPON_ERROR';
};
type StoreCouponTemplate = {
  type: 'cart/coupon/STORE_COUPON_TEMPLATE';
  data: V2CouponTemplate;
};
type ExpandHeaderCart = {
  type: 'cart/header/EXPAND';
  isHeaderCartExpanded: boolean;
};
export type CartAction =
  | { type: '@INIT' }
  | RequestCart
  | RequestCartSuccess
  | RequestCartError
  | AddItem
  | QueueItemAction
  | ClearQueuedItem
  | RemoveItem
  | SetFetching
  | Purchase
  | PurchaseSuccess
  | PurchaseError
  | AddCoupon
  | AddCouponSuccess
  | AddCouponError
  | StoreCouponTemplate
  | CartComparisonSuccess
  | ConfirmedPrescriptions
  | ClearConfirmedPrescriptions
  | UpdateAutoRefillModalState
  | ExpandHeaderCart;
export type CartState = {
  updatedOn: string | null | undefined;
  fetching: boolean;

  /**
   * Add to cart queue is a list of items that should be added to cart
   * When adding and a price tier change requires a user confirmation the item is added to this list
   * Once the user chooses the price tier change we clear the queue
   */
  queuedItem: QueuedItem | null | undefined;
  walletCredit: V2Coupon | null | undefined;
  total: string;
  subtotal: string;
  coupon: V2Coupon | null | undefined;
  couponTemplate: V2CouponTemplate | null | undefined;
  items: Array<Item>;
  hasCoupons: boolean;
  consentMessage: string | null | undefined;
  consentRequired: boolean;
  confirmedPrescriptions: Array<string>;
  isBuyingConsultations: Array<number | string>;
  cartPharmacyNetworkId: number | null | undefined;
  showAutoRefillModal: boolean;
  isHeaderCartExpanded: boolean;
  error: APIError | null;
  id: string;
};

const dispatchSuccess = (dispatch: Dispatch) => (res: V2CartResponse) => {
  return dispatch({
    type: 'cart/REQ_CART_SUCCESS',
    res: res.result,
  });
};

const dispatchError = (dispatch: Dispatch) => (error: APIError) => {
  return dispatch({
    type: 'cart/REQ_CART_ERROR',
    ...error,
  });
};

function setFetching(): SetFetching {
  return {
    type: 'cart/SET_FETCHING',
  };
}

export function updateAutoRefillModalState(): UpdateAutoRefillModalState {
  return {
    type: 'cart/UPDATE_AUTO_REFILL_MODAL_STATE',
  };
}

function orderAsMarketingWantsIt(
  order: V0Order,
  account: AccountState,
  pharmacy: PharmacyState,
  cart: CartState,
  subscriptions: SubscriptionsState
) {
  const freeDiabetesMedications = order.medications.filter((med) => isFreeDiabetesMedication(med.medId));
  const isTelemed = cart.items.some((item) => item.isTelemedEligible);
  const subOrderCount = subscriptions && subscriptions.subscriptions.length;
  const isRevolver = cart.items.some((item) => item.isRecurring);
  return {
    ...order,
    orderCount: account.orderCount,
    subOrderCount: subOrderCount > 0 ? subOrderCount - 1 : null,
    orderId: order.id,
    is_diabetes_eligible: freeDiabetesMedications.length > 0,
    number_diabetes_eligible: freeDiabetesMedications.length,
    isSubs: false,
    // Subs have their own cart at the moment
    isTelemed: isTelemed,
    pharmacyId:
      pharmacy &&
      pharmacy.selectedPharmacy &&
      pharmacy.selectedPharmacy.location &&
      pharmacy.selectedPharmacy.location.id,
    isRevolver: isRevolver,
  };
}

export function purchase(reimbursementConsent = false, useWallet = true): ThunkAction<Promise<any>> {
  return function (dispatch, getState, { blinkAPIClient }): Promise<any> {
    const { telemed, account } = getState();
    const telemedCurrentState = account && account.telemedCurrentState;
    const telemedTreatmentType = telemed && telemed.selectedTreatmentType;
    return blinkAPIClient
      .post('/cart/purchase', {
        reimbursementConsent,
        useWallet,
        telemedCurrentState,
        telemedTreatmentType,
      })
      .then((res) => res.data)
      .then(async (response: PurchaseResponse) => {
        dispatch(getAccountSummary());
        await Promise.all([dispatch(getSubscriptions()), dispatch(getSchedules())]);
        const state = getState();
        dispatch(
          trackEvent(
            'Completed Order',
            orderAsMarketingWantsIt(response.order, state.account, state.pharmacy, state.cart, state.subscriptions)
          )
        );
        // Linkshare tracking for affiliate events
        trackLinkSharePurchase(response.order.id, dispatch);
        return dispatch({
          type: 'cart/PURCHASE_SUCCESS',
          data: response.order,
        });
      })
      .catch((err) => {
        dispatch({
          type: 'cart/PURCHASE_ERROR',
          ...err,
        });

        throw err;
      });
  };
}

export function addCoupon(coupon_code: string): ThunkAction<Promise<APIError>> {
  return function (dispatch, getState, { blinkAPIClient }): Promise<any> {
    return blinkAPIClient
      .post('/coupons', {
        code: coupon_code,
      })
      .then((res) => res.data)
      .then((data) => {
        dispatch(
          requestCart({
            withSetFetching: false,
          })
        );
        // Refreshing account state after adding a coupon
        // To show correct banner if coupon is a referral coupon
        dispatch(getAccountSummary());

        if (data && data.name) {
          dispatch({
            type: 'cart/coupon/STORE_COUPON_TEMPLATE',
            data,
          });
          dispatch(updateBanner(data.name));
        }

        return dispatch({
          type: 'cart/coupon/ADD_COUPON_SUCCESS',
        });
      })
      .catch((err) => {
        dispatch(updateBanner(err.message));
        setTimeout(() => dispatch(updateBanner(null)), 4000);
        return err;
      });
  };
}

export function compareCarts(): ThunkAction<Promise<any>> {
  return function (dispatch, getState, { blinkAPIClient }): Promise<any> {
    const queuedItem = getState().cart.queuedItem;
    const priceId = queuedItem && queuedItem.prices[queuedItem.tier] && queuedItem.prices[queuedItem.tier].priceId;
    const quantity = queuedItem && queuedItem.quantity;
    const medId = queuedItem && queuedItem.medId;
    const item =
      priceId && quantity && medId
        ? [
            {
              price_id: priceId,
              quantity: quantity,
              dosage_id: medId,
            },
          ]
        : [];
    return blinkAPIClient
      .post(`/v2/cart/comparison`, {
        items: item,
      })
      .then((res) => res.data)
      .then((response) => {
        return dispatch({
          type: 'cart/GET_COMPARISON_SUCCESS',
          data: response.result,
        });
      })
      .catch((err) => {
        return err;
      });
  };
}

function itemAsMarketingWantsIt(item: V2CartItem, state: AppState) {
  return {
    medId: item.medId,
    priceId: item.price.basePrice.priceId,
    quantity: item.quantity,
    medNameId: item.medNameId,
    // pharmacyNetworkId is hardcoded for marketing (adwords, facebook, etc.)
    pharmacyNetworkId: pharmacyTierToNetworkId(item.price.priceCategory, state),
    isSubs: false,
  };
}

export function addItems(
  { items, clearCoupons = false, expandCart, pharmacyId, pharmacyBrand, force, clear = false }: V2AddCartItems,
  trackingData: Record<string, any> = {}
): ThunkAction<Promise<any>> {
  return function (dispatch, getState, { blinkAPIClient }): Promise<any> {
    const state = getState();
    dispatch(setFetching());
    dispatch(optimisticallyUpdateInCartStatus(items, null, true));
    items.forEach((item) => (item.isRecurringDefault = true));
    //  ^ eCommerce backend uses this flag + checks medication eligibility to set `is_recurring` on the cart med,
    //  which determines the status for the "autorefill" toggle on HD checkout flow
    const args: V2AddCartItems = {
      items,
      clearCoupons,
    };
    if (pharmacyId) args.pharmacyId = pharmacyId;
    if (pharmacyBrand) args.pharmacyBrand = pharmacyBrand;
    if (force) args.force = force;
    if (clear) args.clear = clear;
    const medIds = items.map((item) => item.medId);
    return blinkAPIClient
      .post(`/v2/cart/items`, args)
      .then((res) => res.data)
      .then((data: V2CartResponse) => data.result)
      .then((cart: V2Cart) => {
        cart.items
          .filter((item) => medIds.includes(item.medId))
          .map((item) =>
            dispatch(trackEvent('Added Product', { ...itemAsMarketingWantsIt(item, state), ...trackingData }))
          );

        if (expandCart) {
          dispatch(expandHeaderCart(true));
        }

        dispatch({
          type: 'cart/REQ_CART_SUCCESS',
          res: cart,
        });
        return cart;
      })
      .catch(dispatchError(dispatch));
  };
}

export function createAddCartItems(
  payload: V2AddCartItems,
  {
    priceCategory,
    smartPharmacy,
    magicPharmacy,
    selectedPharmacy,
  }: {
    priceCategory: string;
    smartPharmacy?: Record<string, any>;
    magicPharmacy?: Record<string, any>;
    selectedPharmacy?: Record<string, any>;
  }
) {
  const addToCartItems: V2AddCartItems = payload;

  // BAU logic for sending pharmacyId (send only for Magic Card and BSD use cases)
  // TODO: when backend is ready, send pharmacyId always (needed for preferred pharmacy feature)
  if (priceCategory === 'local' && smartPharmacy && smartPharmacy.location) {
    addToCartItems.pharmacyId = smartPharmacy.location.id;
  }

  if (isMagicTier(priceCategory) && magicPharmacy && magicPharmacy.location) {
    addToCartItems.pharmacyId = magicPharmacy.location.id;
  }

  // When Preferred Pharmacy is turned on we also want to return pharmacy id for edlp
  if (priceCategory === 'edlp' && selectedPharmacy && !selectedPharmacy.isDelivery && selectedPharmacy.location) {
    addToCartItems.pharmacyId = selectedPharmacy.location.id;
  }

  return addToCartItems;
}

export function updateCartPharmacy({
  pharmacy,
  force,
  pharmacyNetworkId,
}: {
  pharmacy: V2Pharmacy;
  force: boolean;
  pharmacyNetworkId: number | null | undefined;
}): ThunkAction<Promise<any>> {
  return function (dispatch, getState, { blinkAPIClient }): Promise<any> {
    const opts: {
      pharmacyId?: string;
      pharmacyBrand?: string;
      force: boolean;
      pharmacyNetworkId?: number | null | undefined;
    } = {
      force,
    };
    const pharmacyId: string | null | undefined = getPharmacyId(pharmacy);

    if (pharmacyId) {
      opts.pharmacyId = pharmacyId;
    } else if (pharmacy.brand) {
      opts.pharmacyBrand = pharmacy.brand.shortName;
    }

    if (pharmacyNetworkId) {
      opts.pharmacyNetworkId = pharmacyNetworkId;
    }

    dispatch(setFetching());
    const url = opts.pharmacyId ? '/v2/cart/pharmacy' : '/v2/cart/pharmacy_brand';
    return blinkAPIClient
      .post(url, opts)
      .then((res) => res.data)
      .then(dispatchSuccess(dispatch))
      .catch(dispatchError(dispatch));
  };
}

export function removeItem(cartItemId: string): ThunkAction<Promise<any>> {
  return function (dispatch, getState, { blinkAPIClient }): Promise<any> {
    dispatch(setFetching());
    dispatch(optimisticallyUpdateInCartStatus(null, cartItemId, false));
    const cart = getState().cart;
    const items = cart && cart.items;
    const item = items && items.find((cartItem) => cartItem.id === cartItemId);
    return blinkAPIClient
      .delete(`/v2/cart/items/${cartItemId}`)
      .then((res) => res.data)
      .then(dispatchSuccess(dispatch))
      .then(() => {
        if (item != null) {
          dispatch(clearConfirmedCartPrescription(item.medId, item.medNameId));
        }
      })
      .catch(dispatchError(dispatch));
  };
}

export function queueItem(item: QueuedItem): QueueItemAction {
  return {
    type: 'cart/QUEUE_ITEM',
    item,
  };
}

export function clearQueuedItem(): ClearQueuedItem {
  return {
    type: 'cart/CLEAR_QUEUED_ITEM',
  };
}

export function addQueuedItemToCart(
  pharmacy: V2Pharmacy | null | undefined,
  tierOverride?: string
): ThunkAction<Promise<any>> {
  return function (dispatch, getState): Promise<any> {
    // set pharmacy options
    const pharmacyOpts: {
      pharmacyId?: string;
      pharmacyBrand?: string;
    } = {};
    const id = getPharmacyId(pharmacy);

    if (id) {
      pharmacyOpts.pharmacyId = id;
    } else if (pharmacy && pharmacy.brand) {
      pharmacyOpts.pharmacyBrand = pharmacy.brand.shortName;
    }

    const queuedItem: QueuedItem | null | undefined = getState().cart.queuedItem;
    // queuedItem.tier is the original conflict tier. it can be overrided (eg customer changed their mind)
    // it shouldn't be empty - getPharmacyTier is just here as safety/making flow happy
    const tier = tierOverride || (queuedItem && queuedItem.tier) || getPharmacyTier(pharmacy);

    if (
      queuedItem &&
      (isMagicTier(tier) ? queuedItem.prices[tier] || queuedItem.prices['edlp'] : queuedItem.prices[tier])
    ) {
      return dispatch(
        addItems({
          items: [
            {
              medId: queuedItem.medId,
              priceId: queuedItem.prices[tier] ? queuedItem.prices[tier].priceId : queuedItem.prices['edlp'].priceId,
              quantity: queuedItem.quantity,
            },
          ],
          clearCoupons: true,
          force: true,
          ...pharmacyOpts,
        })
      ).then(() => dispatch(clearQueuedItem()));
    }

    return Promise.resolve();
  };
}

export function openConflictResolutionModal(): ThunkAction<Open> {
  return function (dispatch): Open {
    return dispatch(openModal('pharmacy-confirmation'));
  };
}

export function requestCart({
  withSetFetching = true,
}: {
  withSetFetching?: boolean;
} = {}): ThunkAction<Promise<any>> {
  return function requestCartThunk(dispatch, getState, { blinkAPIClient }): Promise<any> {
    if (withSetFetching) {
      dispatch(setFetching());
    }

    return blinkAPIClient
      .get(`/v2/cart`)
      .then((res) => res.data)
      .then(dispatchSuccess(dispatch))
      .catch(dispatchError(dispatch));
  };
}

export type PatchCartItem = {
  itemId: string;
  delete?: boolean;
  rxSrcPharmacyId?: string | null | undefined;
  rxSrcProviderId?: string | null | undefined;
  rxSrcType?: RxSrcType | null | undefined;
  isRecurring?: boolean;
};

export function patchItems(
  items: Array<PatchCartItem>,
  {
    withSetFetching = true,
  }: {
    withSetFetching?: boolean;
  } = {}
): ThunkAction<Promise<any>> {
  return function (dispatch, getState, { blinkAPIClient }): Promise<any> {
    if (withSetFetching) {
      dispatch(setFetching());
    }

    return blinkAPIClient
      .patch(`/v2/cart/items`, {
        items,
      })
      .then((res) => res.data)
      .then(dispatchSuccess(dispatch))
      .catch(dispatchError(dispatch));
  };
}

// We need to differentiate med-name-ids by the ones that user had a
// prescription and the ones that user will need to buy a
// consultation.
type ConfirmationByMedPrescription = Record<string, boolean>;

export const confirmedCartPrescriptions = (
  cartItems: Array<Item>,
  selections: ConfirmationByMedPrescription
): ConfirmedPrescriptions => ({
  type: 'cart/CONFIRMED_HAS_PRESCRIPTIONS',
  data: {
    cartItems,
    selections,
  },
});

function clearConfirmedCartPrescription(medId: string, medNameId: string): ClearConfirmedPrescriptions {
  return {
    type: 'cart/CLEAR_CONFIRMED_HAS_PRESCRIPTIONS',
    medId,
    medNameId,
  };
}

export function lastItemInCartSlug(cartItems: Array<Item>) {
  return cartItems.length && cartItems[cartItems.length - 1].slug;
}

// user can not enroll the same med for auto-refill if its already enrolled
export const getAutoRefillEligibleMeds = (items: Array<Item>, scheduledMedNameIds: Array<string>): Array<Item> =>
  items ? items.filter((item) => item.isAutoRefillEligible && !scheduledMedNameIds.includes(item.medNameId)) : [];
export const expandHeaderCart = (isHeaderCartExpanded: boolean): ExpandHeaderCart => {
  return {
    type: 'cart/header/EXPAND',
    isHeaderCartExpanded,
  };
};

/*************************
 * Helpers
 *************************/
const parseItems = (i: any): Item => ({
  id: i.id,
  medId: i.medId,
  medNameId: i.medNameId,
  name: i.formattedName,
  alternateName: '',
  formulation: i.formattedDescription,
  price: i.price.basePrice.displayValue,
  isSupersaver: i.price.priceCategory === 'local',
  isTelemedEligible: i.isTelemedEligible,
  quantity: i.quantity,
  discountPrice: i.price.priceWithCoupons.displayValue,
  coupon: i.coupon,
  promotionalCategory: i.promotionalCategory,
  couponTemplate: i.couponTemplate,
  warning: i.warning,
  slug: i.slug,
  userMessage: i.userMessage,
  rxSrcPharmacyId: i.rxSrcPharmacyId,
  rxSrcProviderId: i.rxSrcProviderId,
  rxSrcType: i.rxSrcType,
  prescription: i.prescription,
  priceCategory: i.price.priceCategory,
  consultationPrice: i.price.consultationPrice,
  isRecurring: i.isRecurring,
  isAutoRefillEligible: i.isAutoRefillEligible,
});

const hasCoupons = (cart: V2Cart) => {
  return cart.coupon || cart.items.some((med) => med.coupon);
};

// has all refill drugs excluding telemed
export function hasAllRefills(state: AppState) {
  const items = state.cart.items;
  return items.every(
    (item) => item.prescription && item.prescription.hasCloudPharmacyPrescription && !item.isTelemedEligible
  );
}

export function isSeamlessRefillsActive(state: AppState) {
  const items = state.cart.items;
  return Boolean(
    state.pharmacy.selectedPharmacy &&
      state.pharmacy.selectedPharmacy.isDelivery &&
      items.some((item) => item.prescription && item.prescription.hasCloudPharmacyPrescription) &&
      items.every((item) => !item.isTelemedEligible)
  );
}

export function hasMagicMeds(state: AppState) {
  const items = state.cart.items;
  return items.filter((item) => isMagicPriceCategory(item.priceCategory)).length > 0;
}

/*************************
 *************************/
export const initialState: CartState = {
  error: null,
  fetching: false,
  updatedOn: null,
  walletCredit: null,
  coupon: null,
  couponTemplate: null,
  items: [],
  queuedItem: null,
  subtotal: '',
  total: '',
  hasCoupons: false,
  consentMessage: null,
  consentRequired: false,
  isBuyingConsultations: [],
  confirmedPrescriptions: [],
  cartPharmacyNetworkId: null,
  showAutoRefillModal: true,
  isHeaderCartExpanded: false,
  id: '',
};

export default function cartReducer(
  state: CartState = initialState,
  action: CartAction | ReduxPersistAction
): CartState {
  switch (action.type) {
    case 'cart/UPDATE_AUTO_REFILL_MODAL_STATE': {
      return { ...state, showAutoRefillModal: false };
    }

    case 'cart/REQ_CART_SUCCESS': {
      const { res } = action;
      return {
        ...state,
        updatedOn: new Date().toJSON(),
        error: null,
        fetching: false,
        total: res.totalPrice.priceWithCoupons.displayValue,
        subtotal: res.totalPrice.priceWithCoupons.displayValue,
        coupon: res.coupon,
        items: res.items.map(parseItems),
        walletCredit: res.walletCredit,
        hasCoupons: !!hasCoupons(res),
        consentMessage: res.reimbursementConsentMessage,
        consentRequired: res.reimbursementConsentRequired,
        id: res.id,
        cartPharmacyNetworkId: res.cartPharmacyNetworkId,
      };
    }

    case 'cart/CONFIRMED_HAS_PRESCRIPTIONS': {
      // This remembers which `med_name_ids' the user said they
      // *don't* have prescriptions for.  We need this because we
      // conditionally show questions for users that are buying
      // consultations alongside medications.
      const isBuyingConsultations = Object.keys(action.data.selections).filter((k) => !action.data.selections[k]);
      return {
        ...state,
        confirmedPrescriptions: [...action.data.cartItems.map((cartItem) => cartItem.medId)],
        isBuyingConsultations,
      };
    }

    case 'cart/CLEAR_CONFIRMED_HAS_PRESCRIPTIONS': {
      const removedMedId = action && action.medId;
      const removedMedNameId = action && action.medNameId;
      return {
        ...state,
        confirmedPrescriptions: state.confirmedPrescriptions.filter((medId) => medId !== removedMedId),
        isBuyingConsultations: state.isBuyingConsultations.filter((medNameId) => medNameId !== removedMedNameId),
      };
    }

    case 'cart/coupon/STORE_COUPON_TEMPLATE': {
      const { firstName, discount } = action.data;
      return {
        ...state,
        couponTemplate: {
          firstName,
          discount,
        },
      };
    }

    case 'cart/REQ_CART_ERROR':
      return { ...state, fetching: false };

    case 'cart/SET_FETCHING':
      return { ...state, fetching: true };

    case 'cart/QUEUE_ITEM': {
      return { ...state, queuedItem: action.item };
    }

    case 'cart/CLEAR_QUEUED_ITEM': {
      return { ...state, queuedItem: null };
    }

    case 'cart/PURCHASE_SUCCESS':
      return initialState;

    case 'cart/header/EXPAND':
      return { ...state, isHeaderCartExpanded: action.isHeaderCartExpanded };

    default:
      return state;
  }
}
